/**
 * Theme context definition
 *
 * Holds the context object, its types, and the storage key — everything that
 * is NOT a component.
 *
 * Kept separate from ThemeContext.tsx because Fast Refresh only works when a
 * module exports components exclusively; mixing the context object in with the
 * provider silently breaks HMR for every consumer.
 */

import { createContext } from "react";

export type Theme = "light" | "dark" | "system";

/** The two themes actually renderable; "system" always resolves to one. */
export type ResolvedTheme = "light" | "dark";

/** Shared with the inline pre-paint script in index.html. Keep in sync. */
export const THEME_STORAGE_KEY = "attunewell-theme";

export const DARK_QUERY = "(prefers-color-scheme: dark)";

/** Matches --background in each token block, for browser chrome. */
export const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: "#ffffff",
  dark: "#030711",
};

export interface ThemeContextValue {
  /** What the user chose, including "system". */
  theme: Theme;
  /** What is actually rendered right now. */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  /** Flip between light and dark. From "system", moves off it explicitly. */
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(
  undefined
);
